import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Star } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { feedbackService } from '../services/FeedbackService';

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (feedback: FeedbackData) => void;
}

export interface FeedbackData {
  type: 'bug' | 'improvement';
  rating: number;
  severity?: 'critical' | 'medium' | 'low';
  message: string;
  timestamp: string;
}

export default function FeedbackDialog({ open, onClose, onSubmit }: FeedbackDialogProps) {
  const { showToast } = useNotifications();
  const [feedbackType, setFeedbackType] = useState<'bug' | 'improvement'>('improvement');
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [severity, setSeverity] = useState<'critical' | 'medium' | 'low' | null>(null);
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [adoWorkItem, setAdoWorkItem] = useState<{ id: number; url: string; type: string } | null>(null);

  const isValid = feedbackType === 'bug'
    ? (severity !== null && message.trim().length > 0)
    : (rating > 0 || message.trim().length > 0);

  const handleSubmit = async () => {
    if (feedbackType === 'bug') {
      if (!severity || !message.trim()) {
        showToast('Please select severity and describe the bug', 'error');
        return;
      }
    } else if (rating === 0 && !message.trim()) {
      showToast('Please select stars or write your feedback', 'error');
      return;
    }

    setIsSubmitting(true);

    const feedbackData: FeedbackData = {
      type: feedbackType,
      rating: feedbackType === 'bug' ? 0 : rating,
      severity: feedbackType === 'bug' ? severity! : undefined,
      message: message.trim(),
      timestamp: new Date().toISOString(),
    };

    // Submit to Azure DevOps via backend
    const result = await feedbackService.submitFeedback({
      type: feedbackType,
      rating: feedbackType === 'bug' ? 0 : rating,
      severity: feedbackType === 'bug' ? severity! : undefined,
      message,
    });

    if (!result.success) {
      showToast(result.error || 'Error sending feedback', 'error');
      setIsSubmitting(false);
      return;
    }

    // Track ADO work item if created
    if (result.adoWorkItem?.created) {
      setAdoWorkItem({
        id: result.adoWorkItem.id,
        url: result.adoWorkItem.url,
        type: result.adoWorkItem.type,
      });
    }

    // Callback
    onSubmit?.(feedbackData);

    // Show confirmation with notification
    const adoMsg = result.adoWorkItem?.created
      ? ` (ADO ${result.adoWorkItem.type} #${result.adoWorkItem.id} created)`
      : '';
    showToast(`Thank you for your feedback!${adoMsg}`, 'success');
    setIsSubmitting(false);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setAdoWorkItem(null);
      handleReset();
      onClose();
    }, 4000);
  };

  const handleReset = () => {
    setFeedbackType('improvement');
    setRating(0);
    setSeverity(null);
    setMessage('');
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-[#0054A4]">Feedback</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            aria-label="Close feedback dialog"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-gray-800">Thank you for your feedback!</p>
              <p className="text-xs text-gray-600 mt-1">Your comment helps us improve</p>
              {adoWorkItem && (
                <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-700 font-medium">
                    📋 {adoWorkItem.type} #{adoWorkItem.id} created in Azure DevOps
                  </p>
                  <a
                    href={adoWorkItem.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 underline mt-1 inline-block"
                  >
                    View work item →
                  </a>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Feedback Type */}
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-800 mb-3">What type of feedback?</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setFeedbackType('bug')}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      feedbackType === 'bug'
                        ? 'bg-red-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    🐛 Bug
                  </button>
                  <button
                    onClick={() => setFeedbackType('improvement')}
                    className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      feedbackType === 'improvement'
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    💡 Improvement
                  </button>
                </div>
              </div>

              {/* Bug: Severity picker | Others: Star rating */}
              {feedbackType === 'bug' ? (
                <div className="text-center">
                  <p className="text-sm font-semibold text-gray-800 mb-3">How severe is this bug?</p>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setSeverity('critical')}
                      className={`py-3 px-3 rounded-lg text-sm font-medium transition-all border-2 ${
                        severity === 'critical'
                          ? 'bg-red-600 text-white border-red-600 shadow-lg'
                          : 'bg-white text-red-600 border-red-200 hover:border-red-400 hover:bg-red-50'
                      }`}
                    >
                      🔴 Critical
                      <p className={`text-[10px] mt-1 font-normal ${
                        severity === 'critical' ? 'text-red-100' : 'text-gray-400'
                      }`}>App is broken</p>
                    </button>
                    <button
                      onClick={() => setSeverity('medium')}
                      className={`py-3 px-3 rounded-lg text-sm font-medium transition-all border-2 ${
                        severity === 'medium'
                          ? 'bg-amber-500 text-white border-amber-500 shadow-lg'
                          : 'bg-white text-amber-600 border-amber-200 hover:border-amber-400 hover:bg-amber-50'
                      }`}
                    >
                      🟡 Medium
                      <p className={`text-[10px] mt-1 font-normal ${
                        severity === 'medium' ? 'text-amber-100' : 'text-gray-400'
                      }`}>Something is wrong</p>
                    </button>
                    <button
                      onClick={() => setSeverity('low')}
                      className={`py-3 px-3 rounded-lg text-sm font-medium transition-all border-2 ${
                        severity === 'low'
                          ? 'bg-blue-500 text-white border-blue-500 shadow-lg'
                          : 'bg-white text-blue-600 border-blue-200 hover:border-blue-400 hover:bg-blue-50'
                      }`}
                    >
                      🔵 Low
                      <p className={`text-[10px] mt-1 font-normal ${
                        severity === 'low' ? 'text-blue-100' : 'text-gray-400'
                      }`}>Not urgent</p>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-sm font-semibold text-gray-800 mb-3">How's your experience?</p>
                  <div className="flex gap-2 justify-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoveredRating(star)}
                        onMouseLeave={() => setHoveredRating(0)}
                        className="transition-transform hover:scale-110"
                      >
                        <Star
                          className={`w-8 h-8 ${
                            star <= (hoveredRating || rating)
                              ? 'fill-amber-400 text-amber-400'
                              : 'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Message */}
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-800 mb-2">
                  {feedbackType === 'bug' ? 'Describe the bug' : 'Tell us more (optional)'}
                </p>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={feedbackType === 'bug'
                    ? 'What happened? What were you doing when the bug occurred?'
                    : 'Describe your experience, suggestion or issue...'
                  }
                  className="w-full h-24 p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0054A4] resize-none text-sm"
                />
              </div>

              {/* Validator */}
              <div className="text-center">
                <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                  isValid 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {isValid ? (
                    <>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Ready to send</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <span>{feedbackType === 'bug' ? 'Select severity and describe the bug' : 'Select stars or write feedback'}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 justify-center">
                <button
                  onClick={onClose}
                  className="flex-1 py-2 px-4 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!isValid || isSubmitting}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    isSubmitting
                      ? 'bg-gray-400 text-white cursor-wait'
                      : isValid
                        ? 'bg-gradient-to-r from-[#E4002B] to-[#B91C1C] text-white hover:shadow-md'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    'Send Feedback'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
